import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar, roleLabel, avatarUrl } from "@/components/UserAvatar";
import { basePathFor } from "@/components/UserMenu";
import { Button } from "@/components/ui/button";
import { Mail, Phone, CalendarDays, UserCog, MessageSquare, KeyRound } from "lucide-react";

function memberSince(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

export default function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = basePathFor(user?.role);
  const since = memberSince(user?.created_at);

  return (
    <div className="max-w-3xl mx-auto w-full animate-fade-up">
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {user?.cover
          ? <img src={avatarUrl(user.cover)} alt="" className="h-40 sm:h-52 w-full object-cover" />
          : <div className="h-28" style={{ background: "linear-gradient(120deg, #232D4F 0%, #1A2340 55%, #1E9E2A 140%)" }} />}
        <div className="px-6 pb-6">
          <div className="-mt-12 flex flex-col sm:flex-row sm:items-end gap-4">
            <UserAvatar user={user} size={96} className="ring-4 ring-white" />
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-2xl font-bold text-slate-900 truncate" data-testid="profile-name">{user?.name}</h1>
              {user?.profession && <div className="text-sm text-slate-500">{user.profession}</div>}
              <span className="inline-flex mt-1 text-[11px] font-mono uppercase tracking-wider text-[#1E9E2A] bg-green-50 border border-green-100 px-2 py-0.5 rounded-md">
                {roleLabel(user)}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate(`${base}/mensagens`)}>
                <MessageSquare className="h-4 w-4 mr-1.5" /> Mensagens
              </Button>
              <Button variant="outline" onClick={() => navigate(`${base}/conta#senha`)} data-testid="profile-password-button">
                <KeyRound className="h-4 w-4 mr-1.5" /> Alterar senha
              </Button>
              <Button onClick={() => navigate(`${base}/conta`)} className="text-white" style={{ background: "#232D4F" }} data-testid="profile-edit-button">
                <UserCog className="h-4 w-4 mr-1.5" /> Editar
              </Button>
            </div>
          </div>

          {user?.bio && <p className="text-slate-600 mt-6 leading-relaxed whitespace-pre-line">{user.bio}</p>}

          <div className="grid sm:grid-cols-3 gap-3 mt-6">
            <Info icon={Mail} label="E-mail" value={user?.email} />
            <Info icon={Phone} label="Telefone" value={user?.phone || "Não informado"} />
            <Info icon={CalendarDays} label="Membro desde" value={since || "—"} />
          </div>
        </div>
      </div>
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wide font-semibold">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="text-sm text-slate-800 font-medium mt-1 break-all">{value}</div>
  </div>
);
